/**
 * WebhookConfigPage — `/integration/webhooks`.
 *
 * Manage outbound webhook subscriptions via service-audit
 * GET /v1/integration/webhooks, POST, /:id/pause, /:id/resume.
 */
import { type JSX, useCallback, useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';

import { request } from '../../lib/api/client';
import { PageHeader, StatusBadge } from '../shared/StaffUI';
import { env } from '../../lib/env';

interface Webhook {
  id: string;
  ownerId: string;
  eventType: string;
  url: string;
  status: 'ACTIVE' | 'PAUSED' | 'FAILED';
  createdAt: string;
}

const EVENT_TYPES = [
  'package.status_changed', 'package.delivered', 'payment.completed', 'payment.failed',
  'kyc.verified', 'kyc.expired', 'notification.delivered', 'transaction.created',
  'complaint.created', 'complaint.resolved',
] as const;
const DEFAULT_EVENT_TYPE: typeof EVENT_TYPES[number] = 'package.status_changed';

export function WebhookConfigPage(): JSX.Element {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ownerId, setOwnerId] = useState('');
  const [eventType, setEventType] = useState(DEFAULT_EVENT_TYPE);
  const [webhookUrl, setWebhookUrl] = useState('');

  const loadWebhooks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request<{ webhooks: Webhook[] }>(
        env.serviceAuditBaseUrl,
        '/v1/integration/webhooks',
      );
      setWebhooks(data.webhooks);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadWebhooks(); }, [loadWebhooks]);

  async function handleCreate(): Promise<void> {
    if (!ownerId.trim() || !webhookUrl.trim()) return;
    setSaving(true);
    try {
      await request<Webhook>(env.serviceAuditBaseUrl, '/v1/integration/webhooks', {
        method: 'POST',
        body: { ownerId: ownerId.trim(), eventType, url: webhookUrl.trim() },
      });
      setShowModal(false);
      setOwnerId('');
      setWebhookUrl('');
      await loadWebhooks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create webhook');
    } finally {
      setSaving(false);
    }
  }

  async function handlePause(id: string): Promise<void> {
    try {
      await request<unknown>(env.serviceAuditBaseUrl, `/v1/integration/webhooks/${id}/pause`, {
        method: 'POST',
      });
      await loadWebhooks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to pause webhook');
    }
  }

  async function handleResume(id: string): Promise<void> {
    try {
      await request<unknown>(env.serviceAuditBaseUrl, `/v1/integration/webhooks/${id}/resume`, {
        method: 'POST',
      });
      await loadWebhooks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resume webhook');
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Webhook configuration"
        description="Manage outbound webhook subscriptions for integration partners."
        actions={
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white rounded-lg text-sm font-medium"
            data-testid="open-webhook-modal"
          >
            <Plus className="w-4 h-4" /> New webhook
          </button>
        }
      />

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-3 underline">dismiss</button>
        </div>
      ) : null}

      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted text-sm">Loading webhooks…</div>
        ) : webhooks.length === 0 ? (
          <div className="p-8 text-center text-muted text-sm" data-testid="no-webhooks-notice">
            No webhooks configured yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] text-muted uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left">Owner</th>
                <th className="p-3 text-left">Event</th>
                <th className="p-3 text-left">URL</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((w) => (
                <tr key={w.id} className="border-t border-border hover:bg-gray-50" data-testid="webhook-row">
                  <td className="p-3 text-xs font-medium">{w.ownerId}</td>
                  <td className="p-3 font-mono text-xs">{w.eventType}</td>
                  <td className="p-3 font-mono text-xs max-w-[260px] truncate">{w.url}</td>
                  <td className="p-3 text-center"><StatusBadge status={w.status} /></td>
                  <td className="p-3 text-right">
                    {w.status === 'ACTIVE' ? (
                      <button
                        type="button"
                        onClick={() => void handlePause(w.id)}
                        className="px-2 py-1 text-xs font-medium text-amber-600 rounded hover:bg-amber-50"
                        data-testid="pause-webhook-btn"
                      >
                        Pause
                      </button>
                    ) : w.status === 'PAUSED' ? (
                      <button
                        type="button"
                        onClick={() => void handleResume(w.id)}
                        className="px-2 py-1 text-xs font-medium text-green-700 rounded hover:bg-green-50"
                        data-testid="resume-webhook-btn"
                      >
                        Resume
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal ? (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-border w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="text-base font-bold text-gray-900">New webhook</p>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3 text-xs">
              <div>
                <label className="block text-[10px] text-muted uppercase tracking-wider mb-1" htmlFor="owner-id">
                  Owner / partner
                </label>
                <input
                  id="owner-id"
                  type="text"
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  placeholder="Partner name or ID"
                  className="w-full text-sm px-2 py-1.5 border border-border rounded"
                  data-testid="webhook-owner-input"
                />
              </div>
              <div>
                <label className="block text-[10px] text-muted uppercase tracking-wider mb-1" htmlFor="event-type">
                  Event type
                </label>
                <select
                  id="event-type"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value as typeof EVENT_TYPES[number])}
                  className="w-full text-sm px-2 py-1.5 border border-border rounded"
                  data-testid="webhook-event-select"
                >
                  {EVENT_TYPES.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-muted uppercase tracking-wider mb-1" htmlFor="target-url">
                  Target URL
                </label>
                <input
                  id="target-url"
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://example.com/hooks/upmp"
                  className="w-full text-sm px-2 py-1.5 border border-border rounded"
                  data-testid="webhook-url-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={saving || !ownerId.trim() || !webhookUrl.trim()}
                className="px-3 py-1.5 text-sm bg-nipost-green text-white rounded-lg disabled:opacity-50"
                data-testid="webhook-submit-btn"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
